import React, { createContext, useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const navigate = useNavigate();
    const USERS_API = 'http://localhost:5000/users';

    // Kiểm tra trạng thái đăng nhập khi load lại trang
    useEffect(() => {
        const validateStoredUser = async () => {
            // Ưu tiên localStorage (ghi nhớ), fallback sang sessionStorage
            const storedUser = localStorage.getItem('user') || sessionStorage.getItem('user');
            if (!storedUser) return;

            let parsedUser;
            try {
                parsedUser = JSON.parse(storedUser);
                if (!parsedUser || !parsedUser.email) {
                    localStorage.removeItem('user');
                    sessionStorage.removeItem('user');
                    setUser(null);
                    return;
                }
                // Hiển thị phiên ngay lập tức sau khi mở lại app
                setUser(parsedUser);
            } catch (error) {
                console.error('Error parsing stored user:', error);
                localStorage.removeItem('user');
                sessionStorage.removeItem('user');
                setUser(null);
                return;
            }

            try {
                // Nếu user bị xóa khỏi db thì buộc đăng nhập lại
                const res = await axios.get(USERS_API);
                const candidates = Array.isArray(res.data) ? res.data : [];
                const existingUser = candidates.find(u => u.email === parsedUser.email);

                if (!existingUser) {
                    localStorage.removeItem('user');
                    sessionStorage.removeItem('user');
                    setUser(null);
                    navigate('/login');
                    return;
                }

                setUser(existingUser);
                // Chuẩn hóa về localStorage để luôn nhớ sau khi restart app
                localStorage.setItem('user', JSON.stringify(existingUser));
                sessionStorage.removeItem('user');
            } catch (error) {
                // API tạm lỗi thì vẫn giữ phiên hiện tại, không tự logout
                console.warn('Cannot validate stored user from server, keep local session:', error);
            }
        };

        validateStoredUser();
    }, [navigate]);

    // Xử lý Đăng nhập với dữ liệu thật từ JSON-server
    const login = async (email, password) => {
        console.debug('login called with', { email, password });
        try {
            // fetch full list then filter locally to avoid query encoding quirks
            const res = await axios.get(USERS_API);
            console.log('fetched users', res.data);
            const candidates = Array.isArray(res.data) ? res.data : [];
            const loggedInUser = candidates.find(u => u.email === email && u.password === password);

            if (loggedInUser) {
                console.log('login success, user=', loggedInUser);
                setUser(loggedInUser);
                // Luôn ghi nhớ khi đã đăng nhập, chỉ quên khi logout
                localStorage.setItem('user', JSON.stringify(loggedInUser));
                sessionStorage.removeItem('user');
                navigate('/');
            } else {
                console.warn('login failed: no matching user', { email, password });
                alert("Email hoặc mật khẩu không chính xác!");
            }
        } catch (error) {
            console.error("Lỗi kết nối server:", error);
            alert("Kiểm tra JSON Server đang chạy tại http://localhost:5000!");
        }
    };
    const logout = () => {
        setUser(null);
        localStorage.removeItem('user');
        sessionStorage.removeItem('user');
        navigate('/login');
    };

    const resetPassword = async (email, newPassword) => {
        const cleanEmail = (email || '').trim();
        const cleanPassword = (newPassword || '').trim();

        if (!cleanEmail) {
            return { success: false, message: 'Email không hợp lệ.' };
        }

        if (cleanPassword.length < 6) {
            return { success: false, message: 'Mật khẩu mới phải có ít nhất 6 ký tự.' };
        }

        try {
            const res = await axios.get(USERS_API);
            const candidates = Array.isArray(res.data) ? res.data : [];
            const targetUser = candidates.find(u => u.email === cleanEmail);

            if (!targetUser) {
                return { success: false, message: 'Không tìm thấy tài khoản với email này.' };
            }

            await axios.patch(`${USERS_API}/${targetUser.id}`, {
                password: cleanPassword
            });

            // Nếu người dùng hiện tại vừa đổi mật khẩu, đồng bộ lại phiên đang lưu
            if (user && user.email === cleanEmail) {
                const updatedUser = { ...user, password: cleanPassword };
                setUser(updatedUser);
                localStorage.setItem('user', JSON.stringify(updatedUser));
                sessionStorage.removeItem('user');
            }

            return { success: true, message: 'Đổi mật khẩu thành công.' };
        } catch (error) {
            console.error('Reset password failed:', error);
            return {
                success: false,
                message: 'Không thể cập nhật mật khẩu. Vui lòng thử lại sau.'
            };
        }
    };

    return (
        <AuthContext.Provider value={{ user, login, logout, resetPassword }}>
            {children}
        </AuthContext.Provider>
    );
};

// Custom hook để sử dụng AuthContext
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};